import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as MenuIcon } from "icons/threeDots.svg";
import { ReactComponent as BackArrow } from "icons/left_arrow_purple.svg";
import { ReactComponent as BookIcon } from "icons/book.svg";
import { ReactComponent as PenIcon } from "icons/pen-icon.svg";
import { ReactComponent as EyeIcon } from "icons/eye.svg";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { Link, useNavigate } from "react-router-dom";
import { DICTIONARIES_HOME } from "constants/routes";
import {
  selectDictionaries,
  selectDictionary,
  selectSettings,
} from "slices/dictionary/selectors";
import {
  cancelChanges,
  cloneDictionary,
  closeEditMode,
  deleteDictionary,
  exportDictionaryCsv,
  exportDictionaryXml,
  importDictionaryCsv,
  openEditMode,
  saveChanges,
} from "slices/dictionary/actions/dictionary";
import { editDictionary } from "slices/dictionary/dictionarySlice";
import Dropdown, {
  dropdownTypes,
} from "@visiblethread/react-components/dist/components/Dropdown";
import ButtonsDropdownOption from "@visiblethread/react-components/dist/components/Dropdown/ButtonsDropdownOption";
import { hideModal, showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import {
  dictionaryDeleteDictionaryModal,
  dictionarySetAsDefaultModal,
} from "components/Modal/confModalTypes";
import { showGlobalAlert, showInlineAlert } from "slices/app/appSlice";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import { saveButtonError } from "constants/dictionary";

const Header = () => {
  const dictionary = useSelector(selectDictionary);
  const dictionaries = useSelector(selectDictionaries);
  const { name, id, categories, locked, updatedBy } = dictionary;
  // eslint-disable-next-line no-undef
  const { licensed, roles, userName } = UserInfo;
  const lockedByOtherUser = locked && updatedBy !== userName;
  const usedBy = dictionaries.find((dict) => dict.id === id)?.usedBy;
  const [isDataChanged] = useState(false);
  const { isEditMode } = useSelector(selectSettings);
  const [hasErrors, setHasErrors] = useState(false);
  const [hasPermissions, setHasPermissions] = useState(true);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!licensed || !roles.split(",").includes("dictionary")) {
      const error =
        "You cannot edit this dictionary as you do not have edit permissions";
      setHasPermissions(false);
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: error,
        })
      );
    } else if (locked && lockedByOtherUser) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.warning,
          text: `You cannot perform any actions with this dictionary, as someone else (${updatedBy}) is editing it`,
        })
      );
    }
  }, []);

  useEffect(() => {
    if (isEditMode) {
      const input = inputRef.current;
      if (input) {
        const textLength = input.value.length;
        input.setSelectionRange(textLength, textLength);
        input.focus();
      }
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!categories) {
      return;
    }
    for (let i = 0; i < categories.length; i += 1) {
      const category = categories[i];
      if (category?.error) {
        setHasErrors(true);
        return;
      }
      for (let j = 0; j < category.terms.length; j += 1) {
        const term = category.terms[j];
        if (term?.error) {
          setHasErrors(true);
          return;
        }
      }
    }
    setHasErrors(false);
  }, [categories]);

  const handleImportCsvClick = () => {
    const onOk = (formData) => {
      formData.append("name", name);
      formData.append("id", id);
      dispatch(importDictionaryCsv(formData, name, id));
    };

    const importCsvBodyText = () => (
      <div className="dictionary__import_file__body_text">
        <p className="dictionary__import_file__subheading">
          <small>
            Each item in the CSV data will be imported into the matching
            category in the dictionary. If no matching category can be found a
            new one will be created in the dictionary and the term will be added
            to the new category
          </small>
        </p>
        <p className="dictionary__import_file__format">Format example:</p>
        <blockquote>
          <small>
            <span>category,</span>
            <span>term,</span>
            <span>description,</span>
            <span>case sensitive,</span>
            <span>search type,</span>
            <span>color</span>
          </small>
          <br />
          <small>
            <span>category 1,</span>
            <span>term 1,</span>
            <span>description 1,</span>
            <span>TRUE,</span>
            <span>exact,</span>
            <span>#ff0000</span>
          </small>
          <br />
          <small>
            <span>category 2,</span>
            <span>term 2,</span>
            <span>description 2,</span>
            <span>FALSE,</span>
            <span>wildcard,</span>
            <span>#ff0000</span>
          </small>
          <br />
          <small>
            <span>category 3,</span>
            <span>term 3,</span>
            <span>description 3,</span>
            <span>FALSE,</span>
            <span>regex,</span>
            <span>#ff0000</span>
          </small>
        </blockquote>
      </div>
    );

    dispatch(
      showModal(modalTypes.IMPORT_FILE, {
        title: "Import from Text File (csv)",
        accepts: ".csv",
        okText: "Import",
        onOk,
        bodyText: importCsvBodyText(),
      })
    );
  };

  const handleSetDefaultClick = () => {
    dispatch(
      showModal(
        modalTypes.CONFIRMATION,
        dictionarySetAsDefaultModal(name, id, dispatch)
      )
    );
  };
  const handleDeleteDictionaryClick = async () => {
    const isEmpty =
      dictionary.categories.length === 1 &&
      dictionary.categories[0].name === "Default Category";
    if (dictionary.defaultDictionary) {
      dispatch(
        showInlineAlert({
          variant: alertVariants.danger,
          text: "This dictionary is currently set as your default. You need to set a different dictionary as the default first, then try again",
        })
      );
    } else if (isEmpty && !dictionary.defaultDictionary) {
      dispatch(deleteDictionary(id, navigate));
    } else {
      dispatch(
        showModal(
          modalTypes.CONFIRMATION,
          dictionaryDeleteDictionaryModal({
            dictionaryName: name,
            dictionaryId: id,
            folders: usedBy?.folders || [],
            documents: usedBy?.documents || [],
            navigate,
            dispatch,
          })
        )
      );
    }
  };

  const handleCloneDictionaryClick = () => {
    const cloneDictionaryOnOk = (newName) => {
      dispatch(hideModal(modalTypes.TEXT_FIELD_MODAL));
      dispatch(cloneDictionary(id, newName));
    };

    dispatch(
      showModal(modalTypes.TEXT_FIELD_MODAL, {
        inputText: `${name} (copy)`,
        subHeading: "New dictionary name: ",
        title: "Clone Dictionary",
        placeholderText: "Dictionary name",
        buttonText: "Save",
        errorMessage: "",
        onOk: cloneDictionaryOnOk,
        selectTextOnInitialLoad: true,
      })
    );
  };

  const handleExportAsXmlClick = () => {
    dispatch(exportDictionaryXml(id, name));
  };

  const handleExportAsCsvClick = () => {
    const hasTerms = () => {
      for (let i = 0; i < dictionary.categories?.length; i++) {
        const category = dictionary.categories[i];
        if (category.terms.length > 0) {
          return true;
        }
      }
      return false;
    };
    if (hasTerms()) {
      dispatch(exportDictionaryCsv(id, name));
    } else {
      dispatch(
        showGlobalAlert({
          variant: alertVariants.danger,
          text: "Unable to export dictionary with no terms",
        })
      );
    }
  };

  const handleEditClick = () => {
    dispatch(openEditMode());
  };

  const handleSaveClick = () => {
    dispatch(saveChanges(navigate));
  };

  const handleCancelClick = (e) => {
    if (dictionary.id && isEditMode) {
      e.preventDefault();
      dispatch(closeEditMode());
    } else {
      dispatch(cancelChanges());
    }
  };

  const handleNameChange = ({ target: { value } }) => {
    dispatch(
      editDictionary((prevDictionary) => ({
        ...prevDictionary,
        name: value,
      }))
    );
  };

  const menuOptions = [
    {
      content: "Clone Dictionary",
      onClick: handleCloneDictionaryClick,
    },
    {
      content: "Set Selected as Default",
      onClick: handleSetDefaultClick,
    },
    {
      content: "Import from Text File (csv)",
      onClick: handleImportCsvClick,
    },
    {
      content: "Export to Text File (csv)",
      onClick: handleExportAsCsvClick,
    },
    {
      content: "Export to Archive",
      onClick: handleExportAsXmlClick,
    },
    {
      content: "Delete Dictionary",
      onClick: handleDeleteDictionaryClick,
    },
  ];

  const threeDots = () => (
    <Dropdown
      type={dropdownTypes.buttons}
      propsObject={{
        hideArrowIcon: true,
        button: (
          <span>
            <MenuIcon />
          </span>
        ),
        options: menuOptions.map(({ content, onClick, dataTest }) => (
          <ButtonsDropdownOption
            content={content}
            onClick={onClick}
            disabled={!hasPermissions || locked || isEditMode}
            title={
              isEditMode
                ? 'You can only complete this action when in View Mode. Click "Cancel" to exit Edit Mode, and try again.'
                : ""
            }
            data-test={dataTest}
          />
        )),
        buttonVariant: buttonVariants.secondary,
      }}
    />
  );

  return (
    <div
      className="edit_dictionary_page__header"
      data-testid="dictionary-edit-page-header"
    >
      <div className="edit_dictionary_page__header__left_part">
        {isEditMode ? (
          <span data-testid="disabled-back-arrow">
            <BackArrow className="edit_dictionary_page__header__left_part__disabled_back_arrow" />
          </span>
        ) : (
          <Link to={DICTIONARIES_HOME}>
            <BackArrow />
          </Link>
        )}
        <BookIcon className="edit_dictionary_page__header__left_part__book_icon" />
        <div className="edit_dictionary_page__header__left_part__dictionary_name">
          {isEditMode ? (
            <input
              ref={inputRef}
              value={name}
              onChange={handleNameChange}
              className="edit_dictionary_page__header__left_part__dictionary_name__editor"
              aria-label="dictionary-name-input"
            />
          ) : (
            <div className="edit_dictionary_page__header__left_part__dictionary_name__view">
              {name}
            </div>
          )}
        </div>
        <div className="edit_dictionary_page__header__left_part__edit_note">
          {isEditMode ? (
            <>
              <PenIcon
                width={10}
                height={10}
                className="edit_dictionary_page__header__left_part__edit_note__pen_icon"
              />
              <span>You are editing this dictionary</span>
            </>
          ) : (
            <>
              <EyeIcon
                width={14}
                height={14}
                className="edit_dictionary_page__header__left_part__edit_note__pen_icon"
              />
              <span>You are viewing this dictionary</span>
            </>
          )}
        </div>
      </div>
      <div className="edit_dictionary_page__header__right_part">
        <div className="edit_dictionary_page__header__right_part__buttons_block">
          {isEditMode ? (
            <>
              <Button
                content="Save edits"
                variant={buttonVariants.primary}
                disabled={isDataChanged || hasErrors || categories.length === 0}
                onClick={handleSaveClick}
                error={hasErrors ? saveButtonError : ""}
              />
              <Link
                to={DICTIONARIES_HOME}
                className="secondary_link"
                onClick={(e) => handleCancelClick(e)}
              >
                Cancel Edit
              </Link>
            </>
          ) : (
            <Button
              content="Edit Dictionary"
              variant={buttonVariants.primary}
              disabled={!hasPermissions || lockedByOtherUser}
              onClick={handleEditClick}
            />
          )}
          {!isEditMode && threeDots()}
        </div>
      </div>
    </div>
  );
};

export default Header;
